import { getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { CctpTransactionsResponse } from '@vaultsfyi/common/schemas'
import type { Network } from '@vaultsfyi/common/types'
import type { Address } from 'viem'

export type { CctpTransactionsResponse }

interface CctpTransactionsArgs {
  action: 'bridge' | 'claim'
  userAddress: Address | undefined
  srcNetwork: Network
  dstNetwork: Network
  amount?: string
  useForwarding?: boolean
  transferType?: 'standard' | 'fast'
  recipientAddress?: Address
  message?: string
  attestation?: string
  enabled?: boolean
}

const cctpTransactionsQueryKey = ['cctpTransactions'] as const

const cctpTransactionsQueryOptions = ({
  action,
  userAddress,
  srcNetwork,
  dstNetwork,
  amount,
  useForwarding,
  transferType,
  recipientAddress,
  message,
  attestation,
  enabled = true,
}: CctpTransactionsArgs) => {
  const isBridgeReady = action === 'bridge' && !!amount && amount !== '0'
  const isClaimReady = action === 'claim' && !!message && !!attestation

  return queryOptions({
    queryKey: [
      ...cctpTransactionsQueryKey,
      {
        action,
        userAddress,
        srcNetwork,
        dstNetwork,
        amount,
        useForwarding,
        transferType,
        recipientAddress,
        message,
        attestation,
      },
    ] as const,
    queryFn(): Promise<CctpTransactionsResponse> {
      const internalAppSdk = getInternalAppSdk()

      return internalAppSdk.getCctpTransactions({
        action,
        userAddress: userAddress!,
        srcNetwork,
        dstNetwork,
        amount,
        useForwarding,
        transferType,
        recipientAddress,
        message,
        attestation,
      })
    },
    enabled: !!userAddress && enabled && (isBridgeReady || isClaimReady),
    retry: 1,
    staleTime: 30 * SECOND_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
  })
}

export function useCctpTransactionsQuery(args: CctpTransactionsArgs) {
  return useQuery(cctpTransactionsQueryOptions(args))
}
